import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { Layout } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { SignUpBanner } from 'core/ui/components/sign-up-banner/SignUpBanner'
import { MainContent } from 'core/ui/MainContent'

import user from 'legacy/utils/user'

import { isTenderRejected } from '../domain/Tender'
import { useTender } from '../hooks/useTender'
import { EmptyTenderState } from './EmptyTenderState'
import { LoadDetail } from './LoadDetail'

const StyledContentWrapper = styled(Layout.Stack)`
  padding: 0 48px 0 48px;
`

export type TenderPageProps = {
  match: {
    params: {
      id: string
    }
  }
}

export function TenderPage({ match }: TenderPageProps): JSX.Element {
  const { id } = match.params
  const prevId = useRef<string>()
  const { fetchLoad, load, status, statusAttachment, downloadAttachment } = useTender(id)

  const shouldShowSignUpBanner = isTenderRejected(load?.tender) && !user.isLoggedIn()

  useEffect(() => {
    const isFetchComplete = status === RequestStatus.Done

    if (isFetchComplete && load && prevId.current !== id) {
      prevId.current = id
    }
  }, [id, status, load])

  useEffect(() => {
    if (statusAttachment === RequestStatus.Error) {
      toast.error('Failed to view attachment, please try again.')
    }
  }, [statusAttachment])

  async function handleAcceptedOrRejected() {
    return fetchLoad()
  }

  return (
    <MainContent>
      <Layout.Stack gap="spacing-12">
        <AsyncContent
          status={status}
          loadingState={<LoadingState />}
          errorState={<EmptyTenderState id={id} />}
        >
          {load && (
            <StyledContentWrapper gap="spacing-6">
              {shouldShowSignUpBanner && <SignUpBanner />}
              <LoadDetail
                load={load}
                onAccept={handleAcceptedOrRejected}
                onReject={handleAcceptedOrRejected}
                statusAttachment={statusAttachment}
                onAttachmentDownload={downloadAttachment}
              />
            </StyledContentWrapper>
          )}
        </AsyncContent>
      </Layout.Stack>
    </MainContent>
  )
}
